/**
 * QZ Tray integration service for WaiterMan POS.
 * Handles connection, printer discovery, and raw ESC/POS printing
 * via the locally-installed QZ Tray application.
 *
 * QZ Tray must be installed and running on the POS terminal.
 * Download: https://qz.io/download
 */

let _qzLoaded = false;
let _qzConnected = false;

/** Dynamically load qz-tray.js from CDN if not already loaded */
function ensureQzLoaded() {
  return new Promise((resolve, reject) => {
    if (window.qz) { _qzLoaded = true; resolve(); return; }
    if (_qzLoaded) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qz-tray@2/qz-tray.js';
    script.onload = () => { _qzLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load qz-tray.js'));
    document.head.appendChild(script);
  });
}

/** Connect to QZ Tray's local WebSocket */
export async function qzConnect() {
  await ensureQzLoaded();
  if (!window.qz) throw new Error('QZ Tray library not loaded');
  if (_qzConnected && window.qz.websocket.isActive()) return;
  try {
    await window.qz.websocket.connect({ retries: 2, delay: 1 });
    _qzConnected = true;
  } catch (err) {
    _qzConnected = false;
    throw new Error(
      'Cannot connect to QZ Tray. Make sure QZ Tray is installed and running on this device. ' +
      'Download from https://qz.io/download'
    );
  }
}

/** Disconnect from QZ Tray */
export async function qzDisconnect() {
  if (window.qz && window.qz.websocket.isActive()) {
    await window.qz.websocket.disconnect();
    _qzConnected = false;
  }
}

/** Check if QZ Tray is connected */
export function qzIsConnected() {
  return _qzConnected && window.qz && window.qz.websocket.isActive();
}

/** Find all available printers via QZ Tray */
export async function qzFindPrinters() {
  await qzConnect();
  const printers = await window.qz.printers.find();
  return printers; // string array of printer names
}

/** Find a specific printer by name */
export async function qzFindPrinter(name) {
  await qzConnect();
  return window.qz.printers.find(name);
}

/**
 * Send raw ESC/POS content to a printer via QZ Tray.
 * @param {string} printerName - Exact printer name from qzFindPrinters()
 * @param {string} content - Raw ESC/POS string content
 * @returns {{ success: boolean, error?: string }}
 */
export async function qzPrintRaw(printerName, content) {
  try {
    await qzConnect();
    const config = window.qz.configs.create(printerName);
    // Convert string to raw data array
    const data = [content];
    await window.qz.print(config, data);
    return { success: true };
  } catch (err) {
    console.error('QZ Tray print failed:', err);
    return { success: false, error: err.message || String(err) };
  }
}

/**
 * Send a test print to a printer via QZ Tray.
 * @param {string} printerName - Exact printer name
 */
export async function qzTestPrint(printerName) {
  const testData =
    '\x1b\x40' + // ESC @ — Initialize
    '=== QZ TRAY TEST ===\n' +
    'Printer: ' + printerName + '\n' +
    'Connection: OK\n' +
    '====================\n\n\n' +
    '\x1d\x56\x42\x00'; // GS V B 0 — Partial cut
  return qzPrintRaw(printerName, testData);
}
